import { type FC, type ReactNode, useEffect, useState } from 'react';

import type { Socket } from 'socket.io-client';

import SocketContext from '@/contexts/socket/socket-context';
import useGetMe from '@/hooks/use-get-me';
import { getSocketInstance, resetSocketInstance } from '@/utils/api/get-socket-instance';

interface SocketContextProviderProps {
  children: ReactNode;
}

const SocketContextProvider: FC<SocketContextProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);

  const { data: user, isError } = useGetMe();

  const isAuthenticated = !!user && !isError;

  useEffect(() => {
    const socketInstance = getSocketInstance();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !socketInstance.connected && isAuthenticated) {
        socketInstance.connect();
      }
    };

    if (!socketInstance.connected && isAuthenticated) {
      socketInstance.connect();
    }

    setSocket(socketInstance);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      socketInstance.removeAllListeners();

      if (socketInstance.connected) {
        socketInstance.disconnect();
      }

      resetSocketInstance();
    };
  }, [isAuthenticated]);

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};

export default SocketContextProvider;
